import React from 'react';
import { Activity, Brain, Calendar, FileText } from 'lucide-react';
import { APP_NAME } from '../config/appConfig';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';

const pillars = [
  {
    icon: <FileText className="h-6 w-6 text-sky-600" />,
    title: 'Diagnostic workflows',
    description:
      'The platform demonstrates AI-assisted review of uploaded imaging and document files.',
  },
  {
    icon: <Activity className="h-6 w-6 text-sky-600" />,
    title: 'Preventive wellness',
    description:
      'Users can generate structured diet and sleep plans based on lifestyle inputs.',
  },
  {
    icon: <Calendar className="h-6 w-6 text-sky-600" />,
    title: 'Operational readiness',
    description:
      'Scheduling flows show how patient intake can stay simple while collecting meaningful detail.',
  },
  {
    icon: <Brain className="h-6 w-6 text-sky-600" />,
    title: 'Supportive experiences',
    description:
      'Mental wellness chat demonstrates a compassionate, patient-friendly AI interaction model.',
  },
];

const AboutPage: React.FC = () => {
  useDocumentMetadata(
    'About',
    `Learn how ${APP_NAME} brings diagnostics, wellness planning, and care coordination into one healthcare product experience.`
  );

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="container mx-auto max-w-5xl px-6">
        <div className="rounded-3xl bg-white p-10 shadow-sm ring-1 ring-slate-200">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-sky-600">
            About {APP_NAME}
          </p>
          <h1 className="mt-4 text-4xl font-bold text-slate-900">
            A polished healthcare product demo built around real feature flows.
          </h1>
          <p className="mt-6 text-lg leading-8 text-slate-600">
            {APP_NAME} is a full-stack healthcare experience that combines imaging analysis, personalized wellness planning,
            appointment workflows, and mental wellness support in one cohesive application. The project is structured to feel
            production-minded while remaining approachable for interviews, demos, and code reviews.
          </p>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {pillars.map((pillar) => (
              <div key={pillar.title} className="rounded-2xl bg-slate-50 p-6">
                <div className="flex items-center gap-3">
                  {pillar.icon}
                  <h2 className="text-xl font-semibold text-slate-900">{pillar.title}</h2>
                </div>
                <p className="mt-3 text-slate-600">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
